import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../SEO';
import { appConfig } from '../../config/appConfig';
import crossBorderHeroVisual from '../../images/Comp_5.gif';
import fxVisual from '../../images/dashboard.png';
import integrationVisual from '../../images/Comp_4.gif';

const faqs = [
  {
    question: 'Why pursue a cross-border M&A process instead of staying domestic?',
    answer:
      'Cross-border buyers create pricing tension by benchmarking against their home markets, where valuation multiples or strategic fit may be higher. They also bring differentiated synergies—market access, distribution, or technology localization—that unlock higher bids than domestic-only processes.',
  },
  {
    question: 'How do you manage different legal and tax frameworks across the US and Europe?',
    answer:
      'We build an integrated diligence calendar covering US GAAP, IFRS adjustments, tax structuring, employment law, and regulatory filings. Our partner network includes transatlantic legal and tax specialists who align on entity setup, withholding obligations, and closing mechanics early in the process.',
  },
  {
    question: 'Do cross-border deals take longer to close?',
    answer:
      'Properly scoped cross-border deals can close in 5-7 months. Timelines stay on track when workstreams—regulatory, tax, currency hedging, HR—start in parallel. We run weekly cross-functional deal rooms so buyers, attorneys, and advisors execute in sync.',
  },
  {
    question: 'How do you protect confidentiality across regions?',
    answer:
      'We localize NDAs, anonymize materials, and sequence disclosures to vetted buyers only. Data rooms enforce jurisdiction-specific access rights and tracking so sensitive information stays controlled despite multiple jurisdictions.',
  },
];

const structuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'Cross-Border M&A Advisory',
    'name': 'Cross-Border M&A Advisory (US–Europe)',
    'provider': {
      '@type': 'Organization',
      'name': appConfig.site.organizationName,
      'url': appConfig.site.baseUrl,
    },
    'areaServed': ['United States', 'European Union', 'United Kingdom'],
    'audience': {
      '@type': 'Audience',
      'name': 'Mid-market founders pursuing US–Europe cross-border exits',
    },
    'url': `${appConfig.site.baseUrl}/cross-border-mna-us-europe`,
    'description':
      'US–Europe sell-side expertise orchestrating positioning, legal and tax coordination, and buyer access on both sides of the Atlantic.',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD',
      'availability': 'https://schema.org/InStock',
      'url': `${appConfig.site.baseUrl}/valuation`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  },
];

const heroMetrics = [
  {
    label: 'Dual-Track Buyers',
    value: '120+',
    description: 'strategics and PE funds across the US and Europe engaged in our cross-border grid.',
  },
  {
    label: 'Hedged Outcomes',
    value: '96%',
    description: 'transactions with FX strategies that lock cash proceeds for founders.',
  },
  {
    label: 'Parallel Workstreams',
    value: '5',
    description: 'legal, tax, HR, FX, and integration tracks choreographed from kickoff.',
  },
];

const workstreams = [
  {
    title: 'Legal & Regulatory Coordination',
    details:
      'We align US counsel with European specialists to scope entity choices, closing conditions, and regulatory filings at the outset. This framework includes corporate approvals, employment law compliance, and cross-border data transfer protocols.',
  },
  {
    title: 'Tax Structuring & Currency Strategy',
    details:
      'Our tax partners model purchase price allocations, withholding, and repatriation scenarios. We orchestrate FX hedging recommendations so founders know the effective cash outcome at close regardless of currency swings.',
  },
  {
    title: 'Operational & Cultural Integration',
    details:
      'We design integration roadmaps covering governance cadence, go-to-market alignment, and cross-cultural leadership onboarding. Buyers see a plan to harmonize systems and teams before exclusivity begins.',
  },
];

const buyerAccess = [
  'Strategic acquirers headquartered in Europe expanding into North America via tuck-in acquisitions.',
  'US-based companies seeking EU distribution channels, regulatory approvals, or engineering talent.',
  'Transatlantic private equity funds executing dual-platform strategies with teams in both regions.',
  'Family offices and corporate venture arms hungry for cross-border innovation pipelines.',
  'Sovereign-backed investors targeting resilient, export-ready mid-market businesses.',
];

const listItemClasses =
  'flex items-start gap-3 text-sm sm:text-base text-gray-700 leading-relaxed';
const listDotClasses =
  'mt-1.5 h-2 w-2 flex-none rounded-full bg-amiva-primary';

const visualShowcase = [
  {
    image: crossBorderHeroVisual,
    alt: 'Global transaction strategy session spanning US and Europe.',
    label: 'Bidder Heatmap',
    metric: '120+ cross-border buyers',
    caption: 'Side-by-side scorecards highlight appetite, approvals, and timing across regions.',
    gradient: 'from-[#0B1B2E]/70 via-[#1E3A8A]/60 to-[#9333EA]/60',
  },
  {
    image: fxVisual,
    alt: 'Currency hedging dashboard showing EUR/USD scenarios.',
    label: 'Currency Strategy',
    metric: '96% hedged outcomes',
    caption: 'FX models show founders net proceeds in USD, EUR, and GBP under multiple deal mixes.',
    gradient: 'from-[#12274A]/70 via-[#2563EB]/60 to-[#38BDF8]/60',
  },
  {
    image: integrationVisual,
    alt: 'Integration roadmap mapping US and EU workstreams.',
    label: 'Integration Runway',
    metric: '5 parallel tracks',
    caption: 'Workstream dashboards align legal, tax, HR, and go-to-market teams across continents.',
    gradient: 'from-[#0F172A]/70 via-[#1B3A70]/60 to-[#10B981]/60',
  },
];

const highlightStats = [
  {
    label: 'Timeline to Signing',
    value: '5-7 months',
    detail: 'median duration from mandate to cross-border closing when workstreams run in parallel.',
  },
  {
    label: 'Integration Readiness Score',
    value: '82%',
    detail: 'of shortlisted buyers advance to integration planning after AMIVA orchestrates command rooms.',
  },
  {
    label: 'Localized Compliance Coverage',
    value: '100%',
    detail: 'processes include customized NDAs, privacy controls, and data residency safeguards.',
  },
];

const toolkitCards = [
  {
    title: 'Cross-Border Command Center',
    description: 'Single-pane view of legal, tax, HR, and FX workstreams with responsibilities mapped.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 9h6v6H9z" />
      </svg>
    ),
  },
  {
    title: 'Currency Strategy Model',
    description: 'Scenario tools comparing cash, rollover, and hedge outcomes across EUR/USD/GBP.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 9.5h3a2.5 2.5 0 010 5H8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 9.5h-3a2.5 2.5 0 100 5h3" />
      </svg>
    ),
  },
  {
    title: 'Bidder Heat Maps',
    description: 'Scorecards tracking appetite, approvals, and integration readiness for each region.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 9h14M5 15h9" />
      </svg>
    ),
  },
];

const processHighlights = [
  {
    title: 'Dual-Diligence Setup',
    timeline: 'Weeks 1-2',
    steps: ['Align US + EU counsel', 'Entity + tax structuring map', 'Data residency + privacy plan'],
  },
  {
    title: 'Bidder Activation',
    timeline: 'Weeks 3-5',
    steps: ['Time-zone sequenced outreach', 'Localized collateral & KPIs', 'FX + compliance FAQ packs'],
  },
  {
    title: 'Closing Mechanics',
    timeline: 'Weeks 6-9',
    steps: ['Hedge execution & tracking', 'Regulatory submission tracker', 'Integration runway workshops'],
  },
];

const personaProfiles = [
  {
    title: 'US Founders Scaling into Europe',
    stat: 'Global expansion',
    description:
      'Operators with traction in the US seeking European strategics or sponsors to accelerate localization, regulatory clearance, and distribution.',
  },
  {
    title: 'European Leaders Entering the US',
    stat: 'Market entry',
    description:
      'Growth-stage companies in the EU leveraging US buyers for capital, channel access, and regulatory navigation while retaining regional leadership.',
  },
  {
    title: 'Transatlantic Private Equity Platforms',
    stat: 'Dual headquarters',
    description:
      'Funds orchestrating bolt-ons across both continents who need synchronized diligence, tax structuring, and integration governance.',
  },
];

const relatedPlaybooks = [
  {
    title: 'SaaS & Software M&A Advisor',
    description: 'Ideal if your cross-border mandate includes product-led growth or ARR storylines.',
    to: '/saas-mna-advisor',
  },
  {
    title: 'Manufacturing & Precision Machining M&A',
    description: 'For industrial suppliers running global programs and compliance-heavy workstreams.',
    to: '/manufacturing-precision-machining-mna',
  },
  {
    title: 'HVAC & Home Services M&A Advisor',
    description: 'Service operators expanding internationally who need parallel buyer coverage.',
    to: '/hvac-home-services-mna-advisor',
  },
];

export default function CrossBorderAdvisor() {
  return (
    <div className="bg-white">
      <SEOHead
        title="Cross-Border M&A Advisor (US–Europe) | AMIVA Equity Partners"
        description="US–Europe sell-side expertise. AMIVA positions founders for premium outcomes by coordinating legal, tax, currency, and buyer workstreams across both continents."
        keywords={[
          'cross-border M&A advisor',
          'US Europe sell-side advisory',
          'transatlantic M&A',
          'international business sale',
        ]}
        canonical="/cross-border-mna-us-europe"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Industries', path: '/industries' },
          { name: 'Cross-Border M&A Advisory', path: '/cross-border-mna-us-europe' },
        ]}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B192E] via-[#12274A] to-[#1B3A70] text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-[-6rem] -left-28 w-[28rem] h-[28rem] bg-amiva-primary/45 rounded-full blur-3xl"></div>
          <div className="absolute bottom-[-6rem] right-[-4rem] w-[30rem] h-[30rem] bg-amiva-accent/35 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,_3fr)_minmax(0,_2.4fr)] gap-12 items-center">
            <div className="max-w-3xl">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-6">
                Cross-Border M&amp;A Advisory
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
                Orchestrate US–Europe deal tension without losing momentum
              </h1>
              <p className="text-base sm:text-lg text-gray-200 leading-relaxed mb-6">
                We synchronize legal, tax, currency, and integration workstreams so transatlantic
                buyers move in lockstep. Founders keep a single narrative while we localize
                diligence packs, benchmark multiples, and hedge FX exposure.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {heroMetrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-2xl border border-white/15 bg-white/5 p-4 text-center"
                  >
                    <div className="text-2xl font-semibold text-white">{metric.value}</div>
                    <p className="mt-1 text-xs uppercase tracking-[0.28em] text-white/70">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-[11px] leading-relaxed text-white/60">
                      {metric.description}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Link to="/valuation" className="btn-primary px-8 py-4 text-lg font-semibold">
                  Benchmark My Cross-Border Value
                </Link>
                <a
                  href={appConfig.links.calendly}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-base font-semibold text-white hover:text-amiva-accent transition-colors"
                >
                  Discuss cross-border strategy →
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-12 right-0 h-24 w-24 rounded-full bg-amiva-accent/40 blur-xl"></div>
              <div className="absolute bottom-[-2rem] -left-10 h-20 w-20 rounded-full bg-white/20 blur-lg"></div>
              <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/5 shadow-[0_30px_60px_rgba(11,25,46,0.45)]">
                <img
                  src={crossBorderHeroVisual}
                  alt="Cross-border deal team collaborating across regions."
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#12274A]/35 via-[#1B3A70]/10 to-transparent"></div>
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1 text-xs uppercase tracking-[0.32em] text-white">
                  Dual-track control room
                </div>
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                  <p className="text-sm font-semibold text-white">
                    FX hedge locked • <span className="text-white/80">5 regulatory paths cleared</span>
                  </p>
                  <p className="mt-1 text-xs text-white/70">
                    Weekly command rooms align counsel, tax, and integration so time zones do not slow momentum.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Founder Profiles */}
      <section className="py-16 lg:py-20 bg-gray-50" id="founder-profiles">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Founder Snapshot
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Cross-border founder profiles we orchestrate for premium exits
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Whether you are exporting software, industrial capacity, or consumer brands, the
              process flexes to your regulatory, tax, and integration realities.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {personaProfiles.map((profile) => (
              <div
                key={profile.title}
                className="rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  {profile.stat}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{profile.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{profile.description}</p>
                <div className="text-xs text-gray-500">
                  We pre-wire counsel, tax, and integration teams so cross-border buyers stay aligned.
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Transatlantic Visuals
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Keep both sides of the ocean aligned with a single source of truth
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Our dashboards unify buyer heat, currency outcomes, and integration runway so US and
              European teams share the same context—no matter the timezone.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visualShowcase.map((shot) => (
              <div
                key={shot.label}
                className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-gray-900/5 shadow-lg"
              >
                <img
                  src={shot.image}
                  alt={shot.alt}
                  className="h-60 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${shot.gradient} opacity-80 mix-blend-multiply`}
                ></div>
                <div className="absolute inset-0 flex flex-col justify-between p-6">
                  <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/15 px-3 py-1 text-xs uppercase tracking-[0.28em] text-white">
                    {shot.label}
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
                    <div className="text-sm font-semibold text-white">{shot.metric}</div>
                    <p className="mt-1 text-xs text-white/75 leading-relaxed">{shot.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-gradient p-8 md:p-12 rounded-3xl border border-gray-100 shadow-xl">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-10">
              <div>
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-2">
                  Transatlantic deal momentum
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Proof points global buyers expect during diligence
                </h2>
              </div>
              <p className="text-sm md:text-base text-gray-600 max-w-xl">
                Our processes harmonize legal, tax, currency, and integration workstreams so buyers
                on both sides of the Atlantic stay engaged and founders capture full value.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {highlightStats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transition-transform duration-300 hover:-translate-y-1 flex flex-col gap-3 h-full"
                >
                  <div className="text-3xl sm:text-4xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amiva-primary">
                    {stat.label}
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Cross-Border Advantage Dashboard
            </h2>
            <p className="text-base text-gray-600">
              Visual proof of dual-track execution
            </p>
          </div>

          {/* Visual Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {[
              { icon: '🌍', label: 'US Buyer Network', value: 'Strategic Access', sublabel: 'American strategics seeking EU footprint', color: 'blue' },
              { icon: '🇪🇺', label: 'European Buyers', value: 'Market Entry', sublabel: 'EU companies pursuing US expansion', color: 'purple' },
              { icon: '💱', label: 'Currency Hedging', value: 'FX Protection', sublabel: 'Founders insulated from EUR/USD/GBP swings', color: 'green' },
              { icon: '⚖️', label: 'Dual Legal Tracks', value: 'Coordination', sublabel: 'US + EU counsel aligned from day one', color: 'indigo' },
              { icon: '🏦', label: 'Tax Structuring', value: 'Optimization', sublabel: 'Purchase price allocation & withholding scenarios', color: 'yellow' },
              { icon: '⏱️', label: 'Time Zone Management', value: 'Workflow', sublabel: 'Weekly sync meetings across 8-hour spread', color: 'red' },
              { icon: '📋', label: 'Regulatory Compliance', value: 'Multi-Jurisdiction', sublabel: 'GDPR, employment law, data transfers', color: 'pink' },
              { icon: '🎯', label: 'Valuation Premium', value: 'Competitive Tension', sublabel: 'Cross-border bidding drives pricing up', color: 'orange' }
            ].map((metric) => (
              <div key={metric.label} className={`relative bg-gradient-to-br from-blue-50 to-white rounded-3xl border-2 border-blue-200 p-6 hover:border-blue-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
                <div className="text-5xl mb-3 text-center">{metric.icon}</div>
                <div className="text-sm font-bold text-gray-900 mb-2 text-center">{metric.label}</div>
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2 text-center">{metric.value}</div>
                <div className="text-xs text-gray-600 leading-snug">{metric.sublabel}</div>
              </div>
            ))}
          </div>

          {/* Toolkit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {toolkitCards.map((card) => (
              <div key={card.title} className="group relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl border-2 border-blue-200 p-8 hover:border-blue-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full opacity-20 -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-5 group-hover:scale-110 transition-transform duration-300">
                    {card.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{card.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{card.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Cross-Border Command Center
            </h2>
            <p className="text-base text-gray-600">
              5 parallel workstreams orchestrated across continents
            </p>
          </div>
          
          {/* Visual Workstream Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {workstreams.map((item, idx) => (
              <div key={item.title} className="group relative overflow-hidden bg-gradient-to-br from-white to-blue-50 rounded-3xl border-2 border-blue-200 p-8 hover:border-blue-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full opacity-20 -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full text-xl font-bold mb-4">
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.details}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Visual Timeline */}
          <div className="bg-white rounded-3xl border-2 border-gray-200 p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Dual-Track Timeline</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {processHighlights.map((phase) => (
                <div key={phase.title} className="relative">
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border-2 border-blue-200 p-6">
                    <div className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-2">{phase.timeline}</div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">{phase.title}</h4>
                    <ul className="space-y-2">
                      {phase.steps.map((step) => (
                        <li key={step} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-blue-500 flex-none"></span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Buyer Access on Both Sides</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Our buyer intelligence platform tracks hundreds of strategic and financial buyers
                with active cross-border mandates. We know which European corporates currently seek
                US platforms, which US strategics crave European footprint, and which PE funds have
                currency-hedged dry powder. Each outreach campaign is customized with localized
                messaging and relevant metrics (ARR in EUR vs. USD, EBITDA margins under IFRS vs.
                GAAP) so discussions stay on point.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Confidentiality remains paramount. We gate data room access, use localized NDAs, and
                present founders only to buyers with vetted track records. Our coverage extends from
                London and Frankfurt to New York and San Francisco, ensuring a competitive auction
                cycle regardless of geography.
              </p>
            </div>
            <div className="card-gradient p-8 shadow-xl transition-all duration-300 hover:-translate-y-1">
              <ul className="space-y-3">
                {buyerAccess.map((item) => (
                  <li key={item} className={listItemClasses}>
                    <span className={listDotClasses}></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-500 mt-6">
                We maintain reference checks, integration case studies, and cultural compatibility
                notes on each buyer group so we know how they operate before we ever exchange names.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Cross-Border FAQs</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Founders evaluating international exits ask the following questions before launching a
              process. Consider these a preview of how we deploy our cross-border playbook.
            </p>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="card-premium p-6 md:p-8 transition-all duration-300 hover:-translate-y-1"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-amiva-primary transition-colors">
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Link */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Related Playbooks
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Explore AMIVA coverage that complements cross-border processes
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Founders often blend international expansion with vertical software, industrial
              assets, or field operations. These playbooks highlight how we extend the same rigor.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPlaybooks.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="group rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1 hover:border-amiva-primary/40"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  Playbook
                </span>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-amiva-primary">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{item.description}</p>
                <span className="text-sm font-semibold text-amiva-primary group-hover:text-amiva-accent inline-flex items-center gap-2">
                  View details
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 10.5L10.5 3.5M10.5 3.5H4.375M10.5 3.5V9.625"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-amiva-primary to-amiva-accent text-white py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">Let’s architect your cross-border exit.</h2>
            <p className="text-lg text-white/90">
              We will benchmark valuations, map regulatory requirements, and engage both US and
              European buyers to maximize your outcome.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/valuation" className="bg-white text-amiva-primary font-semibold px-6 py-3 rounded-xl text-center">
              Start the Valuation
            </Link>
            <a
              href={appConfig.links.calendly}
              target="_blank"
              rel="noreferrer"
              className="border border-white/70 rounded-xl px-6 py-3 font-semibold text-center hover:bg-white/10 transition"
            >
              Book a Cross-Border Briefing
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
